"use client"

import { cn } from "@/lib/utils"
import { motion } from "framer-motion"

interface ProgressIndicatorProps {
  steps: string[]
  currentStep: number
  onStepClickAction: (step: number) => void
  hideProgressBar?: boolean
  compact?: boolean
}

export function ProgressIndicator({ steps, currentStep, onStepClickAction, hideProgressBar = false, compact = false }: ProgressIndicatorProps) {
  const progressPercent = steps.length > 1 ? (currentStep / (steps.length - 1)) * 100 : 0

  if (compact && hideProgressBar) {
    return (
      <div className="min-w-0 overflow-hidden py-1">
        <div className="h-1 w-full overflow-hidden rounded-full bg-foreground/20" role="progressbar" aria-valuenow={currentStep + 1} aria-valuemin={1} aria-valuemax={steps.length}>
          <motion.div
            className="h-full rounded-full bg-foreground"
            initial={false}
            animate={{ width: `${progressPercent}%` }}
            transition={{ type: "spring", stiffness: 120, damping: 20 }}
          />
        </div>
      </div>
    )
  }

  return (
    <div className={cn("min-w-0 px-1.5", compact ? "pt-1" : "pt-2")}>
      <div className={cn("relative min-w-0", "py-2")}>
        <div className={cn("flex min-w-0 items-center justify-between gap-0.5")}>
          {steps.map((step, index) => (
            <button
              key={step}
              onClick={() => onStepClickAction(index)}
              className={cn(
                "relative z-10 flex shrink-0 flex-col items-center transition-all duration-200",
                index > currentStep ? "opacity-40" : "opacity-100"
              )}
              disabled={index > currentStep}
              aria-label={`Go to ${step}`}
            >
              <motion.div
                className={cn(
                  "shrink-0 rounded-full",
                  compact ? "h-2 w-2" : "h-2.5 w-2.5",
                  index < currentStep ? "bg-foreground/70" : index === currentStep ? "bg-foreground" : "bg-foreground/20"
                )}
                animate={{ scale: index === currentStep ? 1.2 : 1 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
              />
            </button>
          ))}
        </div>

        {!hideProgressBar && (
          <>
            <div
              className={cn(
                "absolute left-0 top-1/2 w-full -translate-y-1/2 rounded-full bg-foreground/15",
                compact ? "h-0.5" : "h-1",
              )}
              aria-hidden
            />
            <motion.div
              className={cn(
                "absolute left-0 top-1/2 -translate-y-1/2 rounded-full bg-foreground",
                compact ? "h-0.5" : "h-1",
              )}
              initial={{ width: 0 }}
              animate={{ width: `${progressPercent}%` }}
              transition={{ type: "spring", stiffness: 120, damping: 20 }}
              aria-hidden
            />
          </>
        )}
      </div>
    </div>
  )
}

